import React, { useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
    downloadVerifiedSchoolsCsv,
    getSchoolPreRegistrationStatus,
    postPreRegistrationUpdateBooks,
    putPreRegistrationFinish,
} from "@backend/PreRegistrationRequests";
import { changeSchoolBookStatusParams, getSchoolsListRequest } from "@backend/SchoolsRequests";
import { WebRoutes } from "@constants/index";
import { objectToQueryString } from "@helpers/utils/utils";
import { selectSchool } from "@reducer/index";

import SchoolReturnPageView from "./SchoolReturnPage.View";

function SchoolReturnPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const tableTemplateRef = useRef(null);

    // @ts-ignore
    const userData = useSelector((state) => state.user.credentials);
    // @ts-ignore
    const selectedSchool = useSelector((state) => state.school);
    /** @type {[SchoolPreRegistrationDataResource, React.Dispatch<SchoolPreRegistrationDataResource>]} */
    const [school, setSchool] = useState(null);

    const [schoolList, setSchoolList] = useState([]);
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [showSaveChangesModal, setShowSaveChangesModal] = useState(false);
    const [changesList, setChangesList] = useState([]);
    const [firstTime, setFirstTime] = useState(true);

    //#region Effect Hooks
    useEffect(() => {
        getSchoolsList();
    }, []);

    useEffect(() => {
        getCurrentSchoolStatus();
    }, [selectedSchool]);

    //#endregion

    //#region Functions
    async function getSchoolStatus(schoolId) {
        const resp = await getSchoolPreRegistrationStatus(schoolId);

        if (!resp.success) {
            throw new Error("Error fetching school pre-registration status.");
        }

        setSchool(resp.data);
    }

    function getCurrentSchoolStatus() {
        return getSchoolStatus(selectedSchool.id);
    }

    /**
     * @param {string} [schoolId]
     */
    async function getSchoolsList(schoolId) {
        const resp = await getSchoolsListRequest(userData.id);

        if (resp.success) {
            setFirstTime(false);
            setSchoolList(resp.data);
            if (schoolId) {
                dispatch(selectSchool(resp.data.find((sc) => sc.id === schoolId)));
            } else {
                dispatch(selectSchool(resp.data[0]));
            }
            if (showCompleteModal) {
                setShowCompleteModal(false);
            }
        }
    }

    async function changeSchoolBookStatus() {
        const params = {
            schoolId: school.id,
            status: !school.bookStatusFinished,
            adminStatus: school.adminBookStatusFinished,
        };

        const resp = await changeSchoolBookStatusParams(objectToQueryString(params, true));

        if (resp.success) {
            getSchoolsList(school.id);
        }
    }

    async function saveChanges() {
        /** @type {SchoolPreRegistrationUpdateResource} */
        const bookUpdateList = {
            schoolId: school.id,
            schoolBookList: changesList.map((/** @type {BookSchoolPreRegResource} */ book) => ({
                schoolBookId: book.id,
                shouldBeReturned: !book.shouldBeReturned,
                shouldTakeOver: !book.shouldTakeOver,
                employeePreRegistrationComment: book.employeePreRegistrationComment,
            })),
        };

        const resp = await postPreRegistrationUpdateBooks(bookUpdateList);

        if (!resp.success) {
            throw new Error("Error updating book status.");
        }

        setShowSaveChangesModal(false);
        setChangesList([]);
        tableTemplateRef.current.getItems();
    }

    //#endregion

    //#region Handlers
    function handleSaveChanges() {
        saveChanges();
    }

    function handleShowSelectModal(value) {
        setShowSelectModal(value);
    }

    function handleBookStatusEditButtonClick() {
        changeSchoolBookStatus();
        getCurrentSchoolStatus();
    }

    function handleSaveChangesTableButtonClick() {
        setShowSaveChangesModal(true);
    }

    /**
     * @param {BookSchoolPreRegResource} book
     */
    function handleStatusSelect(book) {
        const existingEntry = changesList.findIndex((item) => item.id === book.id);

        if (existingEntry !== -1) {
            changesList[existingEntry].shouldBeReturned = !book.shouldBeReturned;
        } else {
            setChangesList([...changesList, { ...book }]);
        }
    }

    /**
     * @param {BookSchoolPreRegResource} book
     */
    function handlePurchaseSelect(book) {
        const existingEntry = changesList.findIndex((item) => item.id === book.id);

        if (existingEntry !== -1) {
            changesList[existingEntry].shouldTakeOver = !book.shouldTakeOver;
        } else {
            setChangesList([...changesList, { ...book }]);
        }
    }

    function handleProfileButtonClick() {
        navigate(WebRoutes.SchoolProfilePage);
    }

    function handleOnSelectSchool(sc) {
        dispatch(selectSchool(sc));
        setChangesList([]);
    }

    /**
     * @param {BookSchoolPreRegResource} book
     * @param {string} comment
     */
    function handleEmployeePreRegistrationCommentInputChange(book, comment) {
        const existingIndex = changesList.findIndex((item) => item.id === book.id);

        if (existingIndex !== -1) {
            if (comment === "") {
                setChangesList([...changesList.filter((item) => item.id !== book.id)]);
            } else {
                const updatedComment = { ...changesList[existingIndex] };
                updatedComment.employeePreRegistrationComment = comment;
                setChangesList([...changesList.filter((item) => item.id !== book.id), updatedComment]);
            }
        } else {
            setChangesList([...changesList, { ...book, employeePreRegistrationComment: comment }]);
        }
    }

    async function handleSchoolVerifyButtonClick() {
        const resp = await putPreRegistrationFinish(school.id);

        if (!resp.success) {
            throw new Error("Error finishing pre-registration.");
        }

        getCurrentSchoolStatus();
        tableTemplateRef.current.getItems();
    }

    async function handleSchoolsExportButtonClick() {
        await downloadVerifiedSchoolsCsv("SchoolPreRegistration.csv");
    }

    function handleCompleteModalComplete() {
        changeSchoolBookStatus();
        getCurrentSchoolStatus();
    }

    //#endregion

    return (
        <SchoolReturnPageView
            schoolList={schoolList}
            school={school}
            showSelectModal={showSelectModal}
            showCompleteModal={showCompleteModal}
            showSaveChangesModal={showSaveChangesModal}
            changesList={changesList}
            firstTime={firstTime}
            tableTemplateRef={tableTemplateRef}
            userData={userData}
            onBookStatusButtonClick={() => setShowCompleteModal(true)}
            onSchoolVerifyButtonClick={handleSchoolVerifyButtonClick}
            onBookStatusEditButtonClick={handleBookStatusEditButtonClick}
            onCompleteModalComplete={handleCompleteModalComplete}
            onCompleteModalHide={() => setShowCompleteModal(false)}
            onPurchaseSelect={handlePurchaseSelect}
            onProfileButtonClick={handleProfileButtonClick}
            onSaveChangesModalHide={() => setShowSaveChangesModal(false)}
            onSaveChangesModalSubmit={handleSaveChanges}
            onSaveChangesTableButtonClick={handleSaveChangesTableButtonClick}
            onEmployeePreRegistrationCommentInputChange={handleEmployeePreRegistrationCommentInputChange}
            onSelectModalHide={() => setShowSelectModal(false)}
            onSelectSchool={handleOnSelectSchool}
            onSelectSchoolClick={() => handleShowSelectModal(true)}
            onStatusSelect={handleStatusSelect}
            onSchoolsExportButtonClick={handleSchoolsExportButtonClick}
        />
    );
}

export default SchoolReturnPage;
